import { brand } from '../../../theme/themePrimitives';
import CustomModal from '../../modals';
import { D7MetaKeys } from '../../../params/prospect';
import { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';

interface SortProspectsModalProps {
    open: boolean;
    onClose: () => void;
    onSortSelect: (name: D7MetaKeys | null) => void
    sortField: D7MetaKeys | null;
    sortOrder: 'asc' | 'desc';
    setSortOrder: (order: 'asc' | 'desc') => void;
}

// type SortedType =
// 'name' | 'address' | 'state' | 'phone' | 'email' | 'website' | 'ssl' | 'mobilefriendly' |
// 'wordpress' | 'shopify' | 'facebook' | 'fbpixel' | 'instagram' | 'igbusiness' | 'twitter' | 'linkedin' | 'linkedInAnalytics' | 'seo' | 'ganalytics' | 'gmarketing';

const metaKeys: {
    label: string;
    value: D7MetaKeys;
}[] = [
    {
        label: 'Name',
        value: 'name'
    },
    {
        label: 'Address',
        value: 'address1'
    },
    {
        label: 'State',
        value: 'region'
    },
    {
        label: 'Phone',
        value: 'phone'
    },
    {
        label: 'Email',
        value: 'email'
    },
    {
        label: 'Website',
        value: 'website'
    },
    {
        label: 'SSL',
        value: 'has_ssl'
    },
    {
        label: 'Mobile-Friendly',
        value: 'mobilefriendly'
    },
    {
        label: 'WordPress',
        value: 'uses_wp'
    },
    {
        label: 'Shopify',
        value: 'uses_shopify'
    },
    {
        label: 'Facebook',
        value: 'facebook'
    },
    {
        label: 'FB Pixel',
        value: 'fbpixel'
    },
    {
        label: 'Instagram',
        value: 'instagram'
    },
    {
        label: 'IG Business',
        value: 'ig_isbusiness'
    },
    {
        label: 'Twitter',
        value: 'twitter'
    },
    {
        label: 'LinkedIn',
        value: 'linkedin'
    },
    {
        label: 'LinkedIn Analytics',
        value: 'lianalytics'
    },
    {
        label: 'SEO Schema',
        value: 'schema'
    },
    {
        label: 'Google Analytics',
        value: 'ganalytics'
    },
    {
        label: 'Google Remarketing',
        value: 'gremarketing'
    }
];

// const metaKeysArrays: MetaKeys[] = [
//     'name',
//     'phone',
//     'website',
//     'email',
//     'category',
//     'address1',
//     'address2',
//     'region',
//     'zip',
//     'country',
//     'yelpstars',
//     'yelpcount',
//     'fbstars',
//     'fbcount',
//     'facebook',
//     'fbpixel',
//     'schema',
//     'gremarketing',
//     'ganalytics',
//     'lianalytics',
//     'uses_wp',
//     'uses_shopify',
//     'mobilefriendly',
//     'twitter',
//     'linkedin',
//     'googlestars',
//     'googlecount',
//     'ig_followers',
//     'ig_follows',
//     'ig_isbusiness',
//     'ig_media_count',
//     'instagram'
// ];

const SortProspectsModal = ({ open, onClose, onSortSelect, sortField, sortOrder, setSortOrder }: SortProspectsModalProps) => {
    const [selectedSortField, setSelectedSortField] = useState<D7MetaKeys | null>(sortField);
    const [selectedSortOrder, setSelectedSortOrder] = useState<'asc' | 'desc'>(sortOrder);

    const handleSortFieldSelect = (field: D7MetaKeys) => {
        setSelectedSortField(field);
    };

    const handleSortOrderSelect = (order: 'asc' | 'desc') => {
        setSelectedSortOrder(order);
    };

    const handleApplySort = () => {
        onSortSelect(selectedSortField);
        setSortOrder(selectedSortOrder);
        onClose();
    };

    const handleNullValue = (value: D7MetaKeys | null) => {
        onSortSelect(value);
        setSelectedSortField(value);
    };

    return (
        <CustomModal open={open} handleClose={onClose} title="Sort Prospects" isBig={true}>
            <Typography variant="body1" fontWeight={500}>Sort By:</Typography>
            <Box display="flex" flexWrap="wrap" gap={1} my={2}>
                {metaKeys.map((field, index) => {
                    return <Box
                        key={index}
                        border="1px solid lightgray"
                        px={2}
                        py="4px"
                        borderRadius="4px"
                        fontWeight={600}
                        bgcolor={selectedSortField === field.value ? brand[300] : 'white'}
                        color={selectedSortField === field.value ? 'white' : 'gray'}
                        onClick={() => (field.value === selectedSortField ? handleNullValue(null) : handleSortFieldSelect(field.value))}
                        sx={{
                            'cursor': 'pointer',
                            'textTransform': 'capitalize',
                            '&:hover': {
                                backgroundColor: selectedSortField === field.value ? brand[300] : brand[100],
                                color: selectedSortField === field.value ? 'white' : brand[300]
                            }
                        }}
                    >
                        {field.label}
                    </Box>;
                })}
            </Box>
            <Typography variant="body1" fontWeight={500}>Sort By Date:</Typography>
            <Box display="flex" mt={2} gap={2}>
                <Box
                    border="1px solid lightgray"
                    px={2}
                    py="4px"
                    borderRadius="4px"
                    bgcolor={selectedSortOrder === 'desc' ? brand[300] : 'white'}
                    color={selectedSortOrder === 'desc' ? 'white' : 'gray'}
                    fontWeight={600}
                    sx={{
                        'cursor': 'pointer',
                        'textTransform': 'capitalize',
                        '&:hover': {
                            backgroundColor: selectedSortOrder === 'desc' ? brand[300] : brand[100],
                            color: selectedSortOrder === 'desc' ? 'white' : brand[300]
                        }
                    }}
                    onClick={() => handleSortOrderSelect('desc')}
                >
                    Newest First
                </Box>
                <Box
                    border="1px solid lightgray"
                    px={2}
                    py="4px"
                    borderRadius="4px"
                    bgcolor={selectedSortOrder === 'asc' ? brand[300] : 'white'}
                    color={selectedSortOrder === 'asc' ? 'white' : 'gray'}
                    fontWeight={600}
                    sx={{
                        'cursor': 'pointer',
                        'textTransform': 'capitalize',
                        '&:hover': {
                            backgroundColor: selectedSortOrder === 'asc' ? brand[300] : brand[100],
                            color: selectedSortOrder === 'asc' ? 'white' : brand[300]
                        }
                    }}
                    onClick={() => handleSortOrderSelect('asc')}
                >
                    Oldest First
                </Box>
            </Box>
            <Box mt={3}>
                <Button variant="contained" color="primary" fullWidth onClick={handleApplySort}>
                    Apply
                </Button>
            </Box>
        </CustomModal>
    );
};

export default SortProspectsModal;
